"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { reaction, values } from "mobx";
import { observer } from "mobx-react";
import classNames from "classnames";

import ElementSpacer from "../utilities/ElementSpacer";
import Filters from "./geo/Filters";
import InteractiveCollectionMap from "../map/InteractiveCollectionMap";

import Block from "../blocks/SchoolBlocks/_Block";
import {
  columnWidth,
  gridTypesEnum,
  renderGoogleLanguage,
} from "../../utils/SchoolBlocksUtilities";
import { normalizeStringWithoutDiacritics } from "../../utils/InternationalStringUtilities";
import { sb_updateGrid } from "../blocks/SchoolBlocks/blockUtils";
import { ErrorBoundary } from "../utilities/rollbar/ErrorBoundary";
import styles from "./styles/PackeryGrid.module.scss";
import { isJson } from "../admin/spinup/utilities";
import { StoneMason } from "./packery/StoneMason";
import { Layout, LayoutItem } from "./packery/utils";
import { useStores } from "../../stores/useStores";
import { useMicrosite } from "../header/microsite/Microsite";

export const maxBlockGridHeight = 10;
export const maxBlockGridWidth = 4;

export type BlockTypeFilter = {
  id: string;
  title: string;
};

export type PackeryGridProps = {
  htmlId: string;
  gridType: gridTypesEnum;
  displayMap?: boolean;
  tags?: [];
  blockTypeFilters?: BlockTypeFilter[];
  rowHeight: number;
  displayFilterByCategory?: boolean;
  displayFilterByBlockType?: boolean;
  displayFilterByText?: boolean;
  textFilterPlaceholder?: string;
  blockPromises?: { [blockId: string]: Promise<any> };
};

const PackeryGrid = observer((props: PackeryGridProps) => {
  const { htmlId, gridType, textFilterPlaceholder = "" } = props;
  const { gridStore, organizationStore, userStore, interfaceStore, i18nStore } =
    useStores();

  const { shouldShowMicrosite } = useMicrosite();

  const locksAreEnabled = useMemo(() => {
    switch (interfaceStore.breakpoint) {
      case "break-point-xs":
        return false;
      case "break-point-sm":
        return false;
      case "break-point-md":
        return false;
      default:
        return true;
    }
  }, [interfaceStore.breakpoint]);

  const maxColumns = useMemo(() => {
    switch (interfaceStore.breakpoint) {
      case "break-point-xs":
        return 1;
      case "break-point-sm":
        return 2;
      case "break-point-md":
        return shouldShowMicrosite ? 2 : 3;
      default:
        return shouldShowMicrosite ? 3 : 4;
    }
  }, [interfaceStore.breakpoint, shouldShowMicrosite]);

  const gridRef = useRef<HTMLDivElement>(null);

  const grid = organizationStore.currentOrganization;

  useEffect(() => {
    const categoryFiltersDisposer = reaction(
      () => [organizationStore.currentOrganization.category_tree],
      () => setCategoryFilters(),
    );

    return () => {
      categoryFiltersDisposer();
    };
  }, []);

  useEffect(() => {
    gridStore.setId(organizationStore.currentOrganization.id);
    gridStore.setGridType(props.gridType);

    if (props.gridType === gridTypesEnum.ALPHA_GRID) {
      // We only sort the blocks alphabetically when it's an ALPHA_GRID, otherwise we use the block insertion order
      gridStore.setMapEnabled(false);
    } else {
      gridStore.setMapEnabled(props.displayMap);
    }

    setCategoryFilters();
    gridStore.blockTypeFilters.setFilters(
      props.blockTypeFilters || [],
      true,
      true,
    );
  }, []);

  const isSearchingGoogleDrive =
    organizationStore.organization.json_data?.settings?.identity?.google
      ?.searchDrive;
  const isSearchingOffice365Drive =
    organizationStore.organization.json_data?.settings?.identity?.office365
      ?.searchDrive;

  const gridClassName = classNames({
    packery: true,
    [styles.packery]: true,
  });

  const displayFilterByCategory = props.displayFilterByCategory;
  const displayFilterByBlockType = props.displayFilterByBlockType;
  const displayFilterByText = props.displayFilterByText;
  const displayFilters =
    displayFilterByCategory || displayFilterByBlockType || displayFilterByText;

  const shouldRemoveSectionBlocks =
    shouldShowMicrosite &&
    values(gridStore.blocks).filter((block) => block.blockType === "section")
      .length > 0;
  const blockObjNames = {};

  const setCategoryFilters = () => {
    gridStore.categoryFilters.setFilters(
      organizationStore.currentOrganization.category_tree || [],
      true,
      true,
    );
  };

  const toggleMap = () => {
    gridStore.toggleMapVisibility();
  };

  const asyncBlocksLoadedMap = useRef(
    values(gridStore.blocks).reduce((acc, curr) => {
      if (curr.loadAsync) {
        acc[curr.id] = !!curr.asyncLoaded;
      }
      return acc;
    }, {}),
  );

  const handleFinalAsyncBlockLoaded = useCallback((blockId) => {
    const googCode = i18nStore.googleCode;
    if (typeof asyncBlocksLoadedMap.current[blockId] === "boolean") {
      asyncBlocksLoadedMap.current[blockId] = true;
    }
    if (Object.values(asyncBlocksLoadedMap.current).every((b) => b)) {
      renderGoogleLanguage(googCode);
    }
  }, []);

  const onDragStop = (
    newLayout: Layout,
    oldItem: LayoutItem,
    newItem: LayoutItem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    placeholder,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    e,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    element,
  ) => {
    // sort all items according to the new layout
    gridStore.sortBlocksManually(newLayout.map((item) => item.i));

    // move the block
    gridStore.relocateBlock(newItem.i, newItem.x, newItem.y);

    sb_updateGrid(gridStore);
  };

  const onResizeStop = (
    newLayout: Layout,
    oldItem: LayoutItem,
    newItem: LayoutItem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    placeholder,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    e,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    element,
  ) => {
    gridStore.resizeBlock(newItem.i, newItem.w, newItem.h);
    sb_updateGrid(gridStore);
  };

  const onDrag = (
    newLayout: Layout,
    oldItem: LayoutItem,
    newItem: LayoutItem,
    placeholder,
    e,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    element,
  ) => {
    if (gridRef.current) {
      const gridRect = gridRef.current.getBoundingClientRect();
      const relativeTop = interfaceStore.scrollPosition + gridRect.top;
      if (interfaceStore.scrollPosition > 0 && e.y < relativeTop) {
        window.scroll({
          top: window.scrollY - 100,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <ErrorBoundary>
      <ElementSpacer />
      {displayFilters && (
        <Filters
          isSearchingGoogleDrive={isSearchingGoogleDrive}
          isSearchingOffice365Drive={isSearchingOffice365Drive}
          categoryFilters={displayFilterByCategory}
          blockTypeFilters={displayFilterByBlockType}
          textFilter={displayFilterByText}
          textFilterPlaceholder={textFilterPlaceholder}
          toggleMap={toggleMap}
        />
      )}
      <InteractiveCollectionMap enabled={props.displayMap} />
      <main
        id={htmlId}
        className={gridClassName}
        role="main"
        style={{
          paddingTop: gridType === gridTypesEnum.ALPHA_GRID ? "4rem" : 0,
        }}
        data-id={grid.id}
        data-grid-id={grid.id}
      >
        <StoneMason
          rowHeight={props.rowHeight}
          width={columnWidth * maxColumns}
          margin={[14, 14]}
          useCSSTransforms={false} // important so we don't see blocks transition in on initial client render
          draggableHandle={".sb-move-icon"}
          resizeHandles={userStore.editor ? ["se", "s", "e"] : []}
          cols={maxColumns}
          onDragStop={onDragStop}
          onResizeStop={onResizeStop}
          onDrag={onDrag}
          // @ts-ignore
          innerRef={gridRef}
        >
          {values(gridStore.blocks)
            .filter((block) => {
              if (shouldShowMicrosite && shouldRemoveSectionBlocks) {
                return block.blockType !== "section";
              }
              return gridStore.blockVisibility.get(block.id)?.isVisible;
            })
            .map((blockObj) => {
              let sizeY = blockObj.sizeY,
                sizeX = Math.min(blockObj.sizeX, maxColumns);
              if (
                sizeX === null ||
                sizeX < 1 ||
                interfaceStore.breakpoint === "break-point-xs"
              ) {
                sizeX = 1;
              }
              if (
                !sizeY ||
                sizeY < 1 ||
                interfaceStore.breakpoint === "break-point-xs"
              ) {
                // if on small screen, block is bigger than 2, AND it's not a banner/button (defined by blockObj.sizeX > 1), display height of 2
                // canUseDOM is here because when rehydrating the DOM, sizeY won't update appropriately on the client. breakpoint is always xs on the server
                let json_data = blockObj.blockModel.json_data;
                if (typeof json_data === "string" && isJson(json_data))
                  json_data = JSON.parse(json_data);

                if (
                  blockObj.blockType === "message" &&
                  json_data.block.settings.items?.[0]?.image &&
                  !json_data.block.settings.items?.[0].message &&
                  blockObj.sizeX > 1
                ) {
                  // handle banners/buttons
                  sizeY = 1;
                } else if (sizeY > 2 && !blockObj.expanded) {
                  sizeY = 2;
                }
              }
              // if name is not in blockObjNames and not hidden, it's first
              let isFirst = false;

              // This is tricky as we internationalize things.  If the first character is a digit, we want it
              // to fall under our header for '#'.  Other characters (emoji, non-latin alphabet, etc) just just
              // use the first character and move on.  Except diacritics should be grouped into the same heading
              // as their regular latin equivalent.  So A and Á both fall under the heading A.
              const firstCharacter = (
                blockObj.blockType === "person"
                  ? blockObj.blockModel.lname?.[0]
                  : blockObj.filterString?.[0]
              )?.toLocaleUpperCase();
              const firstCharacterIsLetter = firstCharacter
                ? /[\D]/.test(firstCharacter)
                : null;
              const normalizedFirstCharacter = firstCharacterIsLetter
                ? normalizeStringWithoutDiacritics(
                    firstCharacter,
                  ).toLocaleUpperCase()
                : "#";
              if (!blockObjNames[normalizedFirstCharacter]) {
                isFirst = true;
                blockObjNames[normalizedFirstCharacter] = true;
              }

              const screenIndependentMaxColumns = shouldShowMicrosite ? 3 : 4;
              const isStaticGrid = props.gridType !== gridTypesEnum.FLEX_GRID;
              const shouldBeLockedBasedOnGrid =
                !isStaticGrid &&
                locksAreEnabled &&
                blockObj.positionLocked &&
                maxColumns === screenIndependentMaxColumns &&
                maxColumns >= blockObj.posX + blockObj.sizeX;
              const positionIsMutable =
                !isStaticGrid && maxColumns === screenIndependentMaxColumns;

              return (
                <Block
                  key={blockObj.id}
                  blockObj={blockObj}
                  isFirst={isFirst}
                  gridType={props.gridType}
                  locksAreEnabled={locksAreEnabled}
                  outsideGrid={false}
                  maxColumns={maxColumns}
                  rowHeight={props.rowHeight}
                  normalizedFirstCharacter={normalizedFirstCharacter}
                  sizeX={sizeX}
                  sizeY={sizeY}
                  tickLoadedAsync={handleFinalAsyncBlockLoaded}
                  isBlockPreview={false}
                  hasMicrosite={shouldShowMicrosite || false}
                  // StoneMason props
                  maxH={maxBlockGridHeight}
                  maxW={maxBlockGridWidth}
                  w={sizeX}
                  h={sizeY}
                  static={shouldBeLockedBasedOnGrid}
                  x={blockObj.posX}
                  y={blockObj.posY}
                  isResizable={positionIsMutable && !shouldBeLockedBasedOnGrid}
                  isDraggable={positionIsMutable && !shouldBeLockedBasedOnGrid}
                  initialBlockPromise={
                    props.blockPromises?.[blockObj.id] ?? null
                  }
                />
              );
            })}
        </StoneMason>
      </main>
    </ErrorBoundary>
  );
});

export default PackeryGrid;
